// El «proyecto activo» del panel postventa v2 (design/claude-design/
// panel-postventa-v2/README.md §Navegación, decisión 4 de 2026-09-29).
//
// Las rutas antiguas `/observaciones[?revision=pendiente]` y
// `/observaciones/:id` ya no montan pantalla propia para una vertical con
// ciclo de observaciones: redirigen a las observaciones del proyecto que
// el usuario estaba mirando. Ese «estaba mirando» es lo único que vive
// acá — un id en sessionStorage con UN solo punto de escritura
// (`markProjectVisited`, llamado por DetalleProyecto al montar), y una
// lectura que nunca confía en el valor guardado a ciegas: el id tiene que
// seguir existiendo en la cartera que el servidor devuelve hoy, o se
// descarta (un proyecto borrado, un cambio de organización en la misma
// pestaña).
//
// sessionStorage y no localStorage a propósito: el contexto es de la
// pestaña, igual que la navegación que lo produjo, y una pestaña nueva
// arranca sin proyecto activo — cae al único proyecto si hay uno, o a
// Proyectos.

import { useCarteraProyectos } from "./api/queries";

export const ACTIVE_PROJECT_STORAGE_KEY = "activeProjectId";

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // Safari en modo privado / storage bloqueado: el acceso mismo lanza.
    return null;
  }
}

/** El único punto de escritura. Un id vacío no pisa el guardado. */
export function markProjectVisited(id: string | undefined): void {
  if (!id) return;
  try {
    storage()?.setItem(ACTIVE_PROJECT_STORAGE_KEY, id);
  } catch {
    // Cuota llena o storage bloqueado: la redirección cae al fallback.
  }
}

export function readStoredProjectId(): string | null {
  try {
    return storage()?.getItem(ACTIVE_PROJECT_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * Resuelve el proyecto activo contra la cartera real.
 *
 *   - `undefined` mientras la cartera carga (el llamador no renderiza
 *     nada — nunca una redirección prematura a Proyectos);
 *   - el id guardado, si existe en la cartera;
 *   - si no, el único proyecto cuando hay exactamente uno;
 *   - `null` en cualquier otro caso (sin proyectos, varios y ninguno
 *     visitado, o la cartera falló).
 */
export function useActiveProjectId(): string | null | undefined {
  const { data, isError } = useCarteraProyectos();
  if (isError) return null;
  if (data === undefined) return undefined;
  const stored = readStoredProjectId();
  if (stored !== null && data.items.some((p) => p.id === stored)) return stored;
  if (data.items.length === 1) return data.items[0]!.id;
  return null;
}
